import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import {
    FiBarChart2,
    FiDollarSign,
    FiDownload,
    FiEye,
    FiTrendingUp,
    FiTruck,
    FiUsers
} from 'react-icons/fi';
import {
    Area,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import AnalyticsService from '../../services/analyticsService';

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('last30days');
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState({
    stats: {},
    salesTrend: [],
    carCategories: []
  });

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        setLoading(true);
        const [stats, salesTrend, categories] = await Promise.all([
          AnalyticsService.getDashboardStats(),
          AnalyticsService.getSalesTrend(),
          AnalyticsService.getCarCategories()
        ]);

        setAnalyticsData({
          stats: stats.data,
          salesTrend: salesTrend.data,
          carCategories: categories.data
        });
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [timeRange]);

  // Mock data for various analytics
  const performanceData = [
    { month: 'Jan', views: 45000, inquiries: 3200, sales: 65, revenue: 2400000 },
    { month: 'Feb', views: 42000, inquiries: 2800, sales: 59, revenue: 1800000 },
    { month: 'Mar', views: 55000, inquiries: 4100, sales: 80, revenue: 3200000 },
    { month: 'Apr', views: 58000, inquiries: 4500, sales: 81, revenue: 3400000 },
    { month: 'May', views: 48000, inquiries: 3600, sales: 56, revenue: 2200000 },
    { month: 'Jun', views: 62000, inquiries: 5200, sales: 95, revenue: 4100000 },
  ];

  const brandPerformance = [
    { brand: 'Maruti Suzuki', sales: 25, revenue: 850000, avgPrice: 650000 },
    { brand: 'Hyundai', sales: 20, revenue: 1200000, avgPrice: 750000 },
    { brand: 'Tata', sales: 15, revenue: 950000, avgPrice: 820000 },
    { brand: 'Honda', sales: 12, revenue: 980000, avgPrice: 720000 },
    { brand: 'Toyota', sales: 10, revenue: 1250000, avgPrice: 1200000 },
    { brand: 'Mahindra', sales: 8, revenue: 750000, avgPrice: 950000 },
  ];

  const geographicData = [
    { city: 'Mumbai', sales: 35, revenue: 1800000 },
    { city: 'Delhi', sales: 28, revenue: 1500000 },
    { city: 'Bangalore', sales: 22, revenue: 1200000 },
    { city: 'Chennai', sales: 18, revenue: 980000 },
    { city: 'Hyderabad', sales: 15, revenue: 850000 },
    { city: 'Pune', sales: 12, revenue: 720000 },
  ];

  const userBehaviorData = [
    { day: 'Mon', sessions: 1200, pageViews: 4800, avgTime: 240 },
    { day: 'Tue', sessions: 1400, pageViews: 5600, avgTime: 280 },
    { day: 'Wed', sessions: 1350, pageViews: 5400, avgTime: 260 },
    { day: 'Thu', sessions: 1600, pageViews: 6400, avgTime: 320 },
    { day: 'Fri', sessions: 1800, pageViews: 7200, avgTime: 350 },
    { day: 'Sat', sessions: 2200, pageViews: 8800, avgTime: 420 },
    { day: 'Sun', sessions: 1900, pageViews: 7600, avgTime: 380 },
  ];

  const conversionData = [
    { stage: 'Views', value: 62000 },
    { stage: 'Inquiries', value: 5200 },
    { stage: 'Test Drives', value: 850 },
    { stage: 'Negotiations', value: 320 },
    { stage: 'Sales', value: 95 },
  ];

  const priceRangeData = [
    { range: '0-3L', count: 45, color: '#FF8C00' },
    { range: '3-5L', count: 35, color: '#FF6B35' },
    { range: '5-8L', count: 28, color: '#FFD700' },
    { range: '8-12L', count: 18, color: '#FFA500' },
    { range: '12L+', count: 12, color: '#FF4500' },
  ];

  const sellerPerformanceData = [
    { seller: 'Performance', A: 120, B: 110, C: 160, D: 140, E: 99, F: 85 },
  ];

  const kpiCards = [
    {
      title: 'Total Revenue',
      value: '₹17.6Cr',
      change: '+18.2%',
      trend: 'up',
      icon: FiDollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Cars Sold',
      value: '436',
      change: '+12.5%',
      trend: 'up',
      icon: FiTruck,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Active Listings',
      value: '1,247',
      change: '+8.3%',
      trend: 'up',
      icon: FiEye,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    },
    {
      title: 'Avg. Sale Price',
      value: '₹7.8L',
      change: '+5.7%',
      trend: 'up',
      icon: FiTrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    }
  ];

  const exportData = () => {
    // In a real application, this would generate and download a report
    console.log('Exporting analytics data...');
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Advanced Analytics</h1>
          <p className="mt-2 text-gray-600">Comprehensive insights and performance metrics</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="last7days">Last 7 Days</option>
            <option value="last30days">Last 30 Days</option>
            <option value="last3months">Last 3 Months</option>
            <option value="last6months">Last 6 Months</option>
            <option value="lastyear">Last Year</option>
          </select>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={exportData}
            className="inline-flex items-center px-4 py-2 gradient-bg text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <FiDownload className="mr-2 h-4 w-4" />
            Export Report
          </motion.button>
        </div>
      </motion.div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {kpiCards.map((kpi, index) => (
          <motion.div
            key={kpi.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02, y: -5 }}
            className="bg-white rounded-2xl p-6 card-shadow hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{kpi.title}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{kpi.value}</p>
                <div className="flex items-center mt-2">
                  <span className={`text-sm font-medium ${kpi.color}`}>
                    {kpi.change}
                  </span>
                  <span className="text-sm text-gray-500 ml-1">from last period</span>
                </div>
              </div>
              <div className={`p-3 rounded-xl ${kpi.bgColor}`}>
                <kpi.icon className={`h-8 w-8 ${kpi.color}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Main Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Performance Over Time */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl p-6 card-shadow"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Performance Trends</h3>
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid rgba(255, 140, 0, 0.2)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Area 
                yAxisId="left"
                type="monotone" 
                dataKey="views" 
                fill="rgba(255, 140, 0, 0.1)" 
                stroke="#FF8C00" 
              />
              <Bar yAxisId="right" dataKey="sales" fill="#FF6B35" />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="inquiries" 
                stroke="#FFD700" 
                strokeWidth={3}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Brand Performance */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl p-6 card-shadow"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Brand Performance</h3>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={brandPerformance}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="brand" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip 
                formatter={(value, name) => [
                  name === 'revenue' ? `₹${(value/100000).toFixed(1)}L` : value,
                  name === 'revenue' ? 'Revenue' : 'Sales'
                ]}
              />
              <Bar dataKey="sales" fill="#FF8C00" />
              <Bar dataKey="revenue" fill="#FF6B35" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Secondary Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Geographic Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl p-6 card-shadow"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Geographic Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={geographicData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="sales"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {geographicData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={`hsl(${index * 45 + 25}, 70%, 60%)`} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Price Range Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-2xl p-6 card-shadow"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Price Range Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={priceRangeData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
                label={({ range, percent }) => `${range} (${(percent * 100).toFixed(0)}%)`}
              >
                {priceRangeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Conversion Funnel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-2xl p-6 card-shadow"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Conversion Funnel</h3>
          <div className="space-y-4">
            {conversionData.map((stage, index) => {
              const width = (stage.value / conversionData[0].value) * 100;
              const conversionRate = index > 0 ? ((stage.value / conversionData[index - 1].value) * 100).toFixed(1) : 100;
              
              return (
                <div key={stage.stage} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">{stage.stage}</span>
                    <span className="text-sm text-gray-500">{stage.value.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-6 relative">
                    <div 
                      className="h-6 rounded-full gradient-bg flex items-center justify-end pr-2"
                      style={{ width: `${width}%` }}
                    >
                      {index > 0 && (
                        <span className="text-xs text-white font-medium">
                          {conversionRate}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* User Behavior Analytics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="bg-white rounded-2xl p-6 card-shadow"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Weekly User Behavior</h3>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={userBehaviorData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid rgba(255, 140, 0, 0.2)',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Line 
              yAxisId="left"
              type="monotone" 
              dataKey="sessions" 
              stroke="#FF8C00" 
              strokeWidth={3}
              name="Sessions"
            />
            <Line 
              yAxisId="left"
              type="monotone" 
              dataKey="pageViews" 
              stroke="#FF6B35" 
              strokeWidth={3}
              name="Page Views"
            />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="avgTime" 
              stroke="#FFD700" 
              strokeWidth={3}
              name="Avg Time (sec)"
            />
          </LineChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Detailed Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Performing Listings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="bg-white rounded-2xl p-6 card-shadow"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Top Performing Listings</h3>
          <div className="space-y-4">
            {[
              { car: '2022 Honda City', views: 2450, inquiries: 89, price: '12.5L' },
              { car: '2021 Swift Dzire', views: 2200, inquiries: 76, price: '8.2L' },
              { car: '2023 Tata Nexon EV', views: 1980, inquiries: 65, price: '15.8L' },
              { car: '2020 Hyundai Creta', views: 1850, inquiries: 58, price: '14.2L' },
            ].map((listing, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-medium text-gray-900">{listing.car}</p>
                  <p className="text-sm text-gray-600">{listing.views} views • {listing.inquiries} inquiries</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-orange-600">₹{listing.price}</p>
                  <p className="text-xs text-gray-500">Price</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Recent Activities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="bg-white rounded-2xl p-6 card-shadow"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Performance Insights</h3>
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center space-x-3 mb-2">
                <FiTrendingUp className="h-5 w-5 text-green-600" />
                <p className="font-medium text-green-900">Sales Increase</p>
              </div>
              <p className="text-green-800 text-sm">
                Car sales increased by 18.2% compared to last month, with luxury cars showing the highest growth.
              </p>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-center space-x-3 mb-2">
                <FiUsers className="h-5 w-5 text-blue-600" />
                <p className="font-medium text-blue-900">User Engagement</p>
              </div>
              <p className="text-blue-800 text-sm">
                Average session duration increased to 5.8 minutes, indicating improved user engagement.
              </p>
            </div>

            <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
              <div className="flex items-center space-x-3 mb-2">
                <FiBarChart2 className="h-5 w-5 text-orange-600" />
                <p className="font-medium text-orange-900">Market Trends</p>
              </div>
              <p className="text-orange-800 text-sm">
                Electric vehicles are gaining popularity, representing 15% of total inquiries this month.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Analytics;